import type { ColumnDef } from '@tanstack/react-table';

import { hasStarted, transformObjectToCamelCase, transformObjectToSnakeCase } from '@utilities';

import type {
  ClientCredit,
  ClientCreditsResponse,
  ClientCreditsUpdate,
  ClientCreditsUpdatePayload,
  ClientSubscriptionResponse,
  SurveySubscription,
  UserCredit,
  UserCreditResponse,
  UserCreditsResponse,
  UserCreditUpdatePayload,
  UserSubscription,
  UserSubscriptionsPage,
  UserSubscriptionsResponse,
} from '../types';
import { CREDIT_TYPES } from './constants';

type UserCreditsItemColumn = ColumnDef<UserCredit, UserCreditValue> & {
  accessor: 'spacer' | keyof UserCredit;
  disableSortBy?: boolean;
  format?: string;
  width: number;
};

type UserCreditValue = number | string | undefined;

export const transformClientCredits = (response?: ClientCreditsResponse | null): ClientCredit[] => {
  const holdForFutureProjects = response?.on_hold_credits || 0;
  const inFlightCredits = response?.in_flight_credits || 0;
  const totalCredits = response?.total_credits || 0;

  const availableCredits = totalCredits - inFlightCredits;

  return [
    {
      isDisplayed: true,
      label: 'Available Credits',
      tooltip: 'Remaining credits - in flight credits',
      type: CREDIT_TYPES.AVAILABLE,
      value: availableCredits,
    },
    {
      isDisplayed: true,
      isUpdateable: true,
      label: 'Hold for Future Projects',
      type: CREDIT_TYPES.HOLD_FOR_FUTURE,
      value: holdForFutureProjects,
    },
    {
      isDisplayed: false,
      label: 'In Flight Credits',
      type: CREDIT_TYPES.IN_FLIGHT,
      value: inFlightCredits,
    },
  ];
};

export const USER_CREDITS_ITEM_COLUMNS: UserCreditsItemColumn[] = [
  {
    accessor: 'display',
    disableSortBy: true,
    footer: 'Total',
    header: 'Name',
    width: 30,
  },
  {
    accessor: 'neg_diff',
    disableSortBy: true,
    header: 'Dif',
    id: 'neg_diff',
    width: 7,
  },
  {
    accessor: 'allocatedCredits',
    format: '0,0.0',
    header: 'Allocated Credits',
    width: 18,
  },
  {
    accessor: 'pos_diff',
    disableSortBy: true,
    header: 'Dif',
    id: 'pos_diff',
    width: 7,
  },
  {
    accessor: 'spacer',
    cell: () => null,
    disableSortBy: true,
    header: '',
    width: 30,
  },
];

export const transformClientCreditsforApi = (
  data: ClientCreditsUpdate
): ClientCreditsUpdatePayload => {
  return transformObjectToSnakeCase({
    inFlightCredits: data.inFlightCredits || 0,
    onHoldCredits: data.holdForFutureProjects || 0,
    totalCredits: data.availableCredits || 0,
  }) as ClientCreditsUpdatePayload;
};

export const transformUserCreditsData = (
  response?: null | UserCreditsResponse
): undefined | UserCredit[] => {
  return response?.results
    ?.map((user: UserCreditResponse) => {
      const transformedUser = transformObjectToCamelCase(user) as Pick<UserCreditResponse, 'id'> & {
        firstName: string;
        lastName: string;
        surveyCredits: number;
      };
      const allocated = transformedUser.surveyCredits ?? 0;
      const starting = transformedUser.surveyCredits ?? 0;
      const diff = allocated - starting;

      return {
        allocatedCredits: allocated,
        diff,
        display: `${transformedUser.firstName} ${transformedUser.lastName}`,
        id: transformedUser.id,
        neg_diff: diff < 0 ? diff : undefined,
        pos_diff: diff > 0 ? diff : undefined,
        startingCredits: starting,
      };
    })
    .sort((a, b) => b.allocatedCredits - a.allocatedCredits);
};

export const transformClientSubscriptionData = (
  response: ClientSubscriptionResponse[] | null | undefined
): SurveySubscription[] => {
  if (!Array.isArray(response)) return [];

  return response.map((subscription) => {
    const transformedSubscription = transformObjectToCamelCase(subscription) as Omit<
      SurveySubscription,
      'currentPeriod' | 'hasFuturePeriods' | 'hasStarted'
    > & {
      currentPeriodCompletesTotal: number;
      currentPeriodCompletesUsed: number;
      currentPeriodEndDate: null | string;
      currentPeriodStartDate: null | string;
      remainingCompletes: number;
    };
    const {
      currentPeriodCompletesTotal: allowedCompletes,
      currentPeriodCompletesUsed: usedCompletes,
      currentPeriodEndDate: endDate,
      currentPeriodStartDate: startDate,
      periodType,
      remainingCompletes,
    } = transformedSubscription;

    // Build the current period from API fields with the current_period_ prefix.
    const currentPeriod =
      endDate && startDate
        ? {
            allowedCompletes,
            endDate,
            periodType,
            remainingCompletes,
            startDate,
            totalCompletes: allowedCompletes,
            usedCompletes,
          }
        : undefined;

    // A subscription can remain active after its current period ends.
    const hasFuturePeriods = Boolean(
      transformedSubscription.endDate &&
      currentPeriod?.endDate &&
      new Date(transformedSubscription.endDate) > new Date(currentPeriod.endDate)
    );

    return {
      ...transformedSubscription,
      currentPeriod,
      hasFuturePeriods,
      hasStarted: hasStarted(transformedSubscription.startDate),
    };
  });
};

export const transformUserCreditsForApi = (data: UserCredit[]): UserCreditUpdatePayload[] => {
  return data.map((user) => {
    return transformObjectToSnakeCase({
      id: user.id,
      surveyCredits: user.allocatedCredits,
    }) as UserCreditUpdatePayload;
  });
};

export const transformUserSubscriptionData = (
  response?: null | UserSubscriptionsResponse
): UserSubscriptionsPage => {
  const transformedResponse = transformObjectToCamelCase(response) as Omit<
    UserSubscriptionsPage,
    'results'
  > & {
    results?: Array<Omit<UserSubscription, 'isSubscribed'> & { active?: boolean }>;
  };

  return {
    count: transformedResponse?.count ?? 0,
    next: transformedResponse?.next ?? null,
    previous: transformedResponse?.previous ?? null,
    results: transformedResponse?.results?.map((user) => {
      return {
        ...user,
        firstName: user.firstName ?? '',
        isSubscribed: user.active ?? false,
        lastName: user.lastName ?? '',
        periodCompletesUsed: user.periodCompletesUsed ?? 0,
        previousPeriodCompletesUsed: user.previousPeriodCompletesUsed ?? 0,
      };
    }),
  };
};
